import { LANGUAGES } from '@vindr/services/constants/common.constant';
import { DefinedColumns } from '@vindr/services/interfaces';
import formatTimeUnit from '@vindr/utils/date/formatTimeUnit';
import getElapsedTime, { Requirement, Unit } from '@vindr/utils/date/getElapsedTime';
import unitToMillis from '@vindr/utils/date/unitToMillis';
import useRefreshComponent from '@vindr/utils/hooks/useRefreshComponent';
import dayjs from 'dayjs';
import { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface CommonElapsedTimeColumnInterface<T> extends Partial<DefinedColumns<T>> {
  dataIndex: keyof T;
  endIndex: keyof T;
  requirement: Requirement;
  interval: number | boolean;
}

export interface ElapsedTimeColumnInterfaceNoShowDetail<T>
  extends CommonElapsedTimeColumnInterface<T> {
  unit?: Unit;
  detailed?: boolean;
  format?: (val: number | string) => string;
}

export interface ElapsedTimeColumnInterfaceShowDetail<T>
  extends CommonElapsedTimeColumnInterface<T> {
  unit: Unit;
  detailed: boolean;
  format: (val: number | string) => string;
}

export type ElapsedTimeColumnProps<T> =
  | ElapsedTimeColumnInterfaceNoShowDetail<T>
  | ElapsedTimeColumnInterfaceShowDetail<T>;

/**
 * startDate: Using from `dataIndex`.
 *
 * endDate: Using from `endIndex`.
 *
 * Specifies the `requirement` for date parameters.
 * - `'all'`: Both `startDate` and `endDate` are required.
 * - `'optional'`: Only `startDate` is required.
 */
const ElapsedTimeColumn = <T,>(props: ElapsedTimeColumnProps<T>): DefinedColumns<T> => {
  const {
    title,
    dataIndex,
    endIndex,
    requirement,
    unit,
    detailed,
    format,
    interval,
    width = 70,
    key = 'wait_time' as keyof T,
    sorter,
    ellipsis = true,
    render,
    ...rest
  } = props || {};

  const { t, i18n } = useTranslation('Vindoc');

  return {
    title: title || t('Wait Time'),
    dataIndex,
    key,
    width: i18n.language === LANGUAGES.VI ? Number(width) + 50 : width,
    sorter,
    ellipsis,
    render:
      render ||
      ((startDate, record) => (
        <UpdateDate
          endDate={record[endIndex]}
          interval={typeof interval === 'number' ? interval : unitToMillis(unit)}
          startDate={startDate}
          {...{ requirement, unit, detailed, format }}
        />
      )),
    ...rest,
  };
};

export default ElapsedTimeColumn;

export const UpdateDate = ({
  endDate,
  startDate,
  interval,
  requirement,
  unit,
  detailed,
  format,
}: {
  endDate?: string;
  interval: number;
  startDate: string;
  requirement: Requirement;
  unit: Unit;
  detailed: boolean;
  format: (val: number | string) => string;
}) => {
  const { t, i18n } = useTranslation('Vindoc');
  const [refresh, setRefresh] = useRefreshComponent();
  const [endDateState, setEndDateState] = useState(endDate);

  if (i18n.language === LANGUAGES.VI) {
    dayjs.locale('vi');
  } else {
    dayjs.locale('en');
  }

  useEffect(() => {
    if (endDate || !interval) return;

    const updateTick = () => {
      setRefresh();

      if (requirement === 'optional') setEndDateState(dayjs().toISOString());
    };

    const intervalId = setInterval(updateTick, interval);

    return () => clearInterval(intervalId);
  }, [interval]);

  const result = getElapsedTime({
    date: startDate,
    compareDate: endDateState,
    detailed,
    requirement,
    unit,
  });

  return (
    <Fragment key={refresh}>
      <span>{typeof format === 'function' ? format(result) : formatTimeUnit(result, unit, t)}</span>
    </Fragment>
  );
};

export const UpdateDateWithPriority = ({
  endDate,
  startDate,
  interval,
  requirement,
  unit,
  detailed,
  format,
  priorityBadge,
}: {
  endDate?: string;
  interval: number;
  startDate: string;
  requirement: Requirement;
  unit: Unit;
  detailed: boolean;
  format: (val: number | string) => string;
  priorityBadge?: {
    show: boolean;
    color?: string;
    text?: string;
  };
}) => {
  const { t, i18n } = useTranslation('Vindoc');
  const [refresh, setRefresh] = useRefreshComponent();
  const [endDateState, setEndDateState] = useState(endDate);

  if (i18n.language === LANGUAGES.VI) {
    dayjs.locale('vi');
  } else {
    dayjs.locale('en');
  }

  useEffect(() => {
    if (endDate || !interval) return;

    const updateTick = () => {
      setRefresh();

      if (requirement === 'optional') setEndDateState(dayjs().toISOString());
    };

    const intervalId = setInterval(updateTick, interval);

    return () => clearInterval(intervalId);
  }, [interval]);

  const result = getElapsedTime({
    date: startDate,
    compareDate: endDateState,
    detailed,
    requirement,
    unit,
  });

  return (
    <div key={refresh} style={{ position: 'relative' }}>
      <span>{typeof format === 'function' ? format(result) : formatTimeUnit(result, unit, t)}</span>
      {priorityBadge?.show && <span
        className="text-white absolute rounded right-0 text-lg leading-none"
        style={{ padding: '2px', top: '-8px', backgroundColor: priorityBadge.color }}
      >
        {priorityBadge.text}
      </span>}
    </div>
  );
};
